// @ts-nocheck
import { IconStar, IconHeart, IconBell } from '@tabler/icons-react';
import { Button, Card, Code, Group, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import { HoverScale, Pulse, Shake, Bounce } from '../../../components/animations';

export default function InteractivePage() {
  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Interactive Animations</Title>
        <Text c="dimmed" size="sm">
          Animations triggered by user interactions - hover, focus, and attention-grabbing effects
        </Text>
      </Stack>

      {/* HoverScale */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>HoverScale</Title>
            <Text size="sm" c="dimmed">
              Scale elements on hover for interactive feedback
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <SimpleGrid cols={3} spacing="md">
              <HoverScale scale={1.05}>
                <Card variant="subtle" p="md" ta="center" style={{ cursor: 'pointer' }}>
                  <Stack gap="xs" align="center">
                    <IconStar size={32} />
                    <Text size="sm">Hover me (1.05x)</Text>
                  </Stack>
                </Card>
              </HoverScale>

              <HoverScale scale={1.1}>
                <Card variant="subtle" p="md" ta="center" style={{ cursor: 'pointer' }}>
                  <Stack gap="xs" align="center">
                    <IconHeart size={32} />
                    <Text size="sm">Hover me (1.1x)</Text>
                  </Stack>
                </Card>
              </HoverScale>

              <HoverScale scale={1.15}>
                <Card variant="subtle" p="md" ta="center" style={{ cursor: 'pointer' }}>
                  <Stack gap="xs" align="center">
                    <IconBell size={32} />
                    <Text size="sm">Hover me (1.15x)</Text>
                  </Stack>
                </Card>
              </HoverScale>
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<HoverScale scale={1.05}>
  <Card variant="subtle">
    <Stack gap="xs" align="center">
      <IconStar size={32} />
      <Text size="sm">Hover me</Text>
    </Stack>
  </Card>
</HoverScale>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • scale - Scale factor on hover (default: 1.05)
              <br />
              • duration - Transition duration in ms (default: 200)
              <br />
              • easing - CSS easing function
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Pulse */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Pulse</Title>
            <Text size="sm" c="dimmed">
              Continuous pulsing animation for drawing attention
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group>
              <Pulse>
                <Button variant="gradient" gradientVariant="primary">
                  New Feature!
                </Button>
              </Pulse>

              <Pulse duration={2000} scale={1.1}>
                <Card variant="outline" p="sm">
                  <Text size="sm" fw={600}>
                    Limited Offer
                  </Text>
                </Card>
              </Pulse>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Pulse>
  <Button variant="gradient">New Feature!</Button>
</Pulse>

<Pulse duration={2000} scale={1.1}>
  <Card variant="outline">
    <Text fw={600}>Limited Offer</Text>
  </Card>
</Pulse>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • duration - Pulse duration in ms (default: 1500)
              <br />
              • scale - Maximum scale during pulse (default: 1.05)
              <br />
              • iterations - 'infinite' or number of pulses
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Shake */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Shake</Title>
            <Text size="sm" c="dimmed">
              Shake animation for error states or attention
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Shake trigger={true} duration={500}>
              <Card variant="subtle" p="md" style={{ borderColor: 'var(--mantine-color-red-6)', borderWidth: 2 }}>
                <Text size="sm" c="red">
                  ⚠️ Invalid input - this field shakes on error
                </Text>
              </Card>
            </Shake>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const [error, setError] = useState(false);

<Shake trigger={error} duration={500}>
  <Card variant="subtle">
    <Text c="red">Invalid input</Text>
  </Card>
</Shake>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • trigger - Boolean or value that triggers shake
              <br />
              • duration - Shake duration in ms (default: 500)
              <br />
              • intensity - Shake distance in pixels (default: 10)
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Bounce */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Bounce</Title>
            <Text size="sm" c="dimmed">
              Bouncing animation for playful interactions
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group>
              <Bounce>
                <Button variant="light">Click me!</Button>
              </Bounce>

              <Bounce duration={1000}>
                <Card variant="gradient" gradientVariant="secondary" p="sm">
                  <Text c="white" size="sm">
                    🎉 Success!
                  </Text>
                </Card>
              </Bounce>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Bounce>
  <Button variant="light">Click me!</Button>
</Bounce>

<Bounce duration={1000}>
  <Card variant="gradient">
    <Text c="white">🎉 Success!</Text>
  </Card>
</Bounce>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • duration - Bounce duration in ms (default: 600)
              <br />
              • height - Bounce height in pixels (default: 20)
              <br />
              • iterations - Number of bounces (default: 1)
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Interactive Cards</Text>
              <Text size="xs" c="dimmed">
                Use HoverScale on clickable cards, buttons, and links to provide visual feedback.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Call-to-Action Buttons</Text>
              <Text size="xs" c="dimmed">
                Apply Pulse to important CTAs, new features, or limited-time offers to draw attention.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Form Validation</Text>
              <Text size="xs" c="dimmed">
                Use Shake on form fields with validation errors to immediately indicate issues.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Success Notifications</Text>
              <Text size="xs" c="dimmed">
                Combine Bounce with success messages or confirmations for delightful feedback.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
